import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faMoon,
  faRightFromBracket,
  faSun,
  faUser,
  faEye,
  faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../hooks/useTheme';
import type { User } from '../../types/user';
import styles from './UserMenu.module.css';

interface UserMenuProps {
  user: User | null;
  isSystemAdmin: boolean;
  viewAllDepartments: boolean;
  onViewAllToggle: (value: boolean) => void;
  onSignOut: () => void;
}

function initials(user: User): string {
  const name = user.full_name?.trim() || user.username;
  const parts = name.split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? parts[parts.length - 1]![0] : '';
  return (first + second).toUpperCase();
}

export default function UserMenu({ user, isSystemAdmin, viewAllDepartments, onViewAllToggle, onSignOut }: UserMenuProps) {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [open]);

  if (!user) return null;

  const roles: string[] = Array.isArray(user.roles) ? user.roles : [];
  const primaryRole = roles[0] || 'USER';

  function handleToggle() {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((v) => !v);
  }

  return (
    <>
      <button
        ref={buttonRef}
        className={styles['user-menu__trigger']}
        type="button"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="user-menu-trigger"
      >
        <span className={styles['user-menu__avatar']}>{initials(user)}</span>
        <span className={styles['user-menu__name']}>{user.full_name || user.username}</span>
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className={styles['user-menu__chevron']}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={styles['user-menu__dropdown']}
            style={{ top: position.top, right: position.right }}
            role="menu"
          >
            <div className={styles['user-menu__identity']}>
              <span className={styles['user-menu__avatar--lg']}>
                <FontAwesomeIcon icon={faUser} />
              </span>
              <div className={styles['user-menu__identity-text']}>
                <strong className={styles['user-menu__full-name']}>
                  {user.full_name || user.username}
                </strong>
                <span className={styles['user-menu__username']}>@{user.username.replace(/@.*$/, '')}</span>
              </div>
              <span className={styles['user-menu__role-chip']}>{primaryRole}</span>
            </div>

            <div className={styles['user-menu__divider']} />

            {isSystemAdmin && (
              <button
                className={styles['user-menu__item']}
                type="button"
                onClick={() => {
                  onViewAllToggle(!viewAllDepartments);
                  setOpen(false);
                }}
                data-testid="user-menu-view-all"
              >
                <FontAwesomeIcon icon={viewAllDepartments ? faEye : faEyeSlash} />
                <span>{viewAllDepartments ? 'Ver departamento activo' : 'Ver todos los departamentos'}</span>
              </button>
            )}

            <Link
              to="/profile"
              className={styles['user-menu__item']}
              onClick={() => setOpen(false)}
            >
              <FontAwesomeIcon icon={faUser} />
              <span>Perfil</span>
            </Link>

            <button
              className={styles['user-menu__item']}
              type="button"
              onClick={() => {
                setOpen(false);
                toggle();
              }}
              data-testid="user-menu-theme"
            >
              <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} />
              <span>{theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>
            </button>

            <div className={styles['user-menu__divider']} />

            <button
              className={`${styles['user-menu__item']} ${styles['user-menu__item--danger']}`}
              type="button"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              data-testid="user-menu-signout"
            >
              <FontAwesomeIcon icon={faRightFromBracket} />
              <span>Cerrar sesion</span>
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
